import type { VatTreatment } from "./vat";
import { api } from "../lib/api";

export interface Client {
  id: string;
  company_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  cc_emails: string[];
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_postcode: string | null;
  address_country: string | null;
  phone: string | null;
  default_currency: string;
  payment_terms_days: number;
  default_vat_rate: string | null;
  default_vat_treatment: VatTreatment | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ClientInput = Omit<
  Client,
  "id" | "company_id" | "archived_at" | "created_at" | "updated_at"
>;

/** Picker-oriented fetch (dropdowns need every option) — asks for the max page size. */
export function listClients(opts: { archived?: boolean; q?: string } = {}): Promise<Client[]> {
  const params = new URLSearchParams({ page_size: "200" });
  if (opts.archived) params.set("archived", "true");
  if (opts.q) params.set("q", opts.q);
  return api.get<Client[]>(`/api/clients?${params.toString()}`);
}

export function listClientsPaged(
  opts: { archived?: boolean; q?: string; page?: number; page_size?: number } = {},
): Promise<{ items: Client[]; total: number }> {
  const params = new URLSearchParams();
  if (opts.archived) params.set("archived", "true");
  if (opts.q) params.set("q", opts.q);
  if (opts.page) params.set("page", String(opts.page));
  if (opts.page_size) params.set("page_size", String(opts.page_size));
  const query = params.toString();
  return api.getPaged<Client>(`/api/clients${query ? `?${query}` : ""}`);
}

export function getClient(id: string): Promise<Client> {
  return api.get<Client>(`/api/clients/${id}`);
}

export function createClient(input: ClientInput): Promise<Client> {
  return api.post<Client>("/api/clients", input);
}

export function updateClient(
  id: string,
  input: ClientInput & { updated_at: string },
): Promise<Client> {
  return api.put<Client>(`/api/clients/${id}`, input);
}

export function archiveClient(id: string): Promise<void> {
  return api.delete(`/api/clients/${id}`);
}
